
import React, { useState, useEffect } from 'react';
import Button from '../base/Button';

interface Document {
  id: string;
  title: string;
  description?: string;
  file_name: string;
  file_type: string;
  file_size: number | null;
  file_content?: string;
  file_url?: string;
  folder_id: string | null;
  folder_name?: string;
  created_at: string;
  uploaded_by: string;
}

interface Folder {
  id: string;
  name: string;
}

interface DocumentListProps {
  refreshTrigger: number;
  userAccessLevel: string;
  userEmail: string;
  selectedFolderId?: string | null;
}

const DocumentList: React.FC<DocumentListProps> = ({
  refreshTrigger,
  userAccessLevel,
  userEmail,
  selectedFolderId,
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterFolder, setFilterFolder] = useState<string>('all');
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [showMoveModal, setShowMoveModal] = useState<Document | null>(null);

  useEffect(() => {
    loadDocuments();
    loadFolders();
  }, [refreshTrigger, userAccessLevel, userEmail]);

  const loadFolders = async () => {
    try {
      const response = await fetch(
        'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/folder-permissions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userEmail,
            action: 'get_user_folders'
          }),
        }
      );

      const data = await response.json();
      console.log('Folders response:', data); // Debug log
      if (response.ok && data.folders) {
        setFolders(data.folders);
        console.log('Folders loaded:', data.folders); // Debug log
      } else {
        console.error('Failed to load folders:', data);
        setFolders([]); // Ensure folders is always an array
      }
    } catch (error) {
      console.error('Error loading folders:', error);
      setFolders([]); // Ensure folders is always an array
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/get-documents',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'list_all',
            userEmail: userEmail,
            userAccessLevel: userAccessLevel,
          }),
        }
      );

      const data = await response.json();
      console.log('Documents response:', data);

      if (response.ok && data.documents) {
        setDocuments(data.documents);
      } else {
        console.error('Failed to load documents:', data);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateDocument = async (
    documentId: string,
    updates: { title?: string; description?: string }
  ) => {
    try {
      const response = await fetch(
        'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/get-documents',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'update',
            documentId,
            updates,
            userEmail: userEmail,
          }),
        }
      );

      if (response.ok) {
        setEditingDocument(null);
        loadDocuments();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update document');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      alert('Error updating document');
    }
  };

  const moveDocument = async (documentId: string, newFolderId: string | null) => {
    try {
      const response = await fetch(
        'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/get-documents',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'move',
            documentId,
            folderId: newFolderId,
            userEmail: userEmail,
          }),
        }
      );

      if (response.ok) {
        setShowMoveModal(null);
        loadDocuments();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to move document');
      }
    } catch (error) {
      console.error('Error moving document:', error);
      alert('Error moving document');
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      if (document.file_content && document.file_type === 'python') {
        const content = atob(document.file_content);
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.file_name;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return;
      }

      if (document.file_url) {
        const response = await fetch(
          'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/image-proxy',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'download',
              filePath: document.file_url,
            }),
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = window.document.createElement('a');
          a.href = url;
          a.download = document.file_name;
          window.document.body.appendChild(a);
          a.click();
          window.document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Error downloading file. Please try again.');
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(
        'https://dcedtfasjgfyjvhypind.supabase.co/functions/v1/get-documents',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'delete',
            documentId,
            userEmail: userEmail,
          }),
        }
      );

      if (response.ok) {
        loadDocuments();
      }
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes || bytes === 0) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredDocuments = (() => {
    let docs = documents;

    if (selectedFolderId !== undefined) {
      if (selectedFolderId === null) {
        return docs;
      } else {
        return docs.filter((doc) => doc.folder_id === selectedFolderId);
      }
    }

    if (filterFolder === 'all') {
      return docs;
    } else if (filterFolder === '') {
      return docs.filter((doc) => doc.folder_id === null);
    } else {
      return docs.filter((doc) => doc.folder_id === filterFolder);
    }
  })();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">All Documents</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">
                Document
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Folder
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Size
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Uploaded
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredDocuments.map((doc) => (
              <tr key={doc.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 w-2/5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center">
                      <i
                        className={`text-sm ${
                          doc.file_type.includes('pdf')
                            ? 'ri-file-pdf-line text-red-500'
                            : doc.file_type.includes('image')
                            ? 'ri-image-line text-green-500'
                            : doc.file_type.includes('word')
                            ? 'ri-file-word-line text-blue-500'
                            : doc.file_type.includes('excel')
                            ? 'ri-file-excel-line text-green-600'
                            : doc.file_type === 'python'
                            ? 'ri-file-code-line text-yellow-500'
                            : doc.file_type.includes('json') ||
                              doc.file_name.endsWith('.json')
                            ? 'ri-file-code-line text-purple-500'
                            : 'ri-file-line text-gray-500'
                        }`}
                      ></i>
                    </div>
                    <div className="ml-2 min-w-0 flex-1">
                      <div className="text-xs font-medium text-gray-900 truncate">
                        {doc.title}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {doc.file_name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 w-1/6">
                  {doc.folder_name || 'No Folder'}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 w-1/6">
                  {formatFileSize(doc.file_size)}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 w-1/6">
                  {formatDate(doc.created_at)}
                </td>
                <td className="px-3 py-2 w-1/6">
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleDownload(doc)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                      title="Download"
                    >
                      <i className="ri-download-line h-3 w-3"></i>
                    </button>

                    {(userAccessLevel === 'master' || userAccessLevel === 'editor') && (
                      <>
                        <button
                          onClick={() => setEditingDocument(doc)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
                          title="Rename"
                        >
                          <i className="ri-edit-line h-3 w-3"></i>
                        </button>

                        <button
                          onClick={() => {
                            console.log('Opening move modal, available folders:', folders); // Debug log
                            setShowMoveModal(doc);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
                          title="Move"
                        >
                          <i className="ri-folder-transfer-line h-3 w-3"></i>
                        </button>

                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line h-3 w-3"></i>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit Document Modal */}
      {editingDocument && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Edit Document
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Title
                  </label>
                  <input
                    type="text"
                    value={editingDocument.title}
                    onChange={(e) =>
                      setEditingDocument({
                        ...editingDocument,
                        title: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editingDocument.description || ''}
                    onChange={(e) =>
                      setEditingDocument({
                        ...editingDocument,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B1F3A]"
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  onClick={() => setEditingDocument(null)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() =>
                    updateDocument(editingDocument.id, {
                      title: editingDocument.title,
                      description: editingDocument.description,
                    })
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Document Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Move Document: {showMoveModal.title}
              </h3>
              
              {/* Debug info */}
              {folders.length === 0 && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-sm text-yellow-800">
                    No folders available. Available folders: {folders.length}
                  </p>
                  <button 
                    onClick={loadFolders}
                    className="mt-2 text-xs bg-yellow-200 hover:bg-yellow-300 px-2 py-1 rounded"
                  >
                    Reload Folders
                  </button>
                </div>
              )}
              
              <div className="space-y-2 max-h-60 overflow-y-auto">
                <button
                  onClick={() => moveDocument(showMoveModal.id, null)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 border"
                >
                  <i className="ri-folder-line mr-2"></i>
                  No Folder (Root)
                </button>
                {folders.length > 0 ? (
                  folders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => moveDocument(showMoveModal.id, folder.id)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 border"
                    >
                      <i className="ri-folder-line mr-2"></i>
                      {folder.name}
                    </button>
                  ))
                ) : (
                  <div className="text-center py-4 text-gray-5 0">
                    <p>No folders available to move to.</p>
                    <p className="text-xs mt-1">Create folders first to organize your documents.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button
                  onClick={() => setShowMoveModal(null)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
